import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Certification {
  id: number;
  name: string;
  issuer: string;
  logo: string;
  alt: string;
  year: string;
}

interface Partnership {
  id: number;
  name: string;
  type: string;
  logo: string;
  alt: string;
  description: string;
}

interface Award {
  id: number;
  title: string;
  organization: string;
  year: string;
  icon: string;
}

interface CertificationsPartnershipsProps {
  certifications: Certification[];
  partnerships: Partnership[];
  awards: Award[];
}

const CertificationsPartnerships: React.FC<CertificationsPartnershipsProps> = ({
  certifications,
  partnerships,
  awards,
}) => {
  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        {/* Certifications */}
        <div className="mb-20">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-4">
              <Icon name="ShieldCheckIcon" size={20} className="text-success" variant="solid" />
              <span className="text-sm font-cta font-semibold text-success">Certifications</span>
            </div>
            
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              Industry-Leading Credentials
            </h2>
            
            <p className="font-body text-lg text-muted-foreground leading-relaxed">
              Our team holds the highest industry certifications, ensuring you receive expert-level service backed by recognized credentials.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {certifications.map((cert) => (
              <div
                key={cert.id}
                className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 p-6 flex flex-col items-center text-center group"
              >
                <div className="w-20 h-20 mb-4 relative overflow-hidden rounded-lg bg-background flex items-center justify-center">
                  <AppImage
                    src={cert.logo}
                    alt={cert.alt}
                    className="w-16 h-16 object-contain transform group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                
                <h3 className="font-headline font-bold text-sm text-primary mb-1">
                  {cert.name}
                </h3>
                <p className="font-body text-xs text-muted-foreground mb-1">
                  {cert.issuer}
                </p>
                <span className="inline-block px-2 py-1 bg-success/10 text-success text-xs font-body font-semibold rounded">
                  {cert.year}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Partnerships */}
        <div className="mb-20">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-brand-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="HandRaisedIcon" size={20} className="text-brand-primary" />
              <span className="text-sm font-cta font-semibold text-brand-primary">Strategic Partnerships</span>
            </div>
            
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              Trusted Technology Partners
            </h2>
            
            <p className="font-body text-lg text-muted-foreground leading-relaxed">
              We collaborate with industry leaders to deliver cutting-edge solutions and ensure access to the latest technologies.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {partnerships.map((partner) => (
              <div
                key={partner.id}
                className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden group"
              >
                <div className="h-32 bg-background flex items-center justify-center p-6 border-b border-border">
                  <AppImage
                    src={partner.logo}
                    alt={partner.alt}
                    className="max-h-16 w-auto object-contain transform group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-headline font-bold text-lg text-primary">
                      {partner.name}
                    </h3>
                    <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-xs font-body font-semibold rounded-full">
                      {partner.type}
                    </span>
                  </div>
                  
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {partner.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Awards & Recognition */}
        <div>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-warning/10 px-4 py-2 rounded-full mb-4">
              <Icon name="TrophyIcon" size={20} className="text-warning" variant="solid" />
              <span className="text-sm font-cta font-semibold text-warning">Awards & Recognition</span>
            </div>
            
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              Recognized Excellence
            </h2>
            
            <p className="font-body text-lg text-muted-foreground leading-relaxed">
              Our commitment to innovation and client success has earned recognition from industry leaders and organizations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {awards.map((award) => (
              <div
                key={award.id}
                className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 p-6 flex items-start space-x-4 group"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-warning to-warning/60 rounded-lg flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300">
                  <Icon name={award.icon as any} size={24} className="text-white" variant="solid" />
                </div>
                
                <div className="flex-1">
                  <h3 className="font-headline font-bold text-lg text-primary mb-1">
                    {award.title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground mb-2">
                    {award.organization}
                  </p>
                  <span className="inline-block px-3 py-1 bg-warning/10 text-warning text-xs font-body font-semibold rounded-full">
                    {award.year}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificationsPartnerships;